import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { DealMilestone, DealStage } from "@/pages/deals/types";
import Text from "@/components/ui/text";
import { camelCase, isEmpty } from "lodash";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { getMilestoneIcon } from "@/pages/settings/components/dealsSettings/StageStudio/StageDetailedView";
import { TFunction } from "i18next";
import { StagePosition } from "@/pages/settings/components/shared/stageStudio/types";
import { getStagePositionOptions } from "@/pages/settings/components/shared/stageStudio/getPositionOptions";

const TRANSLATION_KEY = "settings.deals.stageStudio";

export type newStageFormData = {
  milestone: DealMilestone;
  name: string;
  positionInPipeline: string;
};

interface CreateNewStageProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateStage?: (stageData: newStageFormData) => void;
}

export const getMilestoneOptions = (t: TFunction<"translation", undefined>) =>
  Object.values(DealMilestone)
    .filter((value) => typeof value === "number")
    .map((milestone) => {
      const milestoneId = milestone as DealMilestone;

      return {
        id: milestone as DealMilestone,
        displayName: t(
          `deals.dealView.milestone.${camelCase(DealMilestone[milestoneId])}`
        )
      };
    });

export const getPositionOptions = (
  stages: DealStage[],
  t: TFunction<"translation", undefined>,
  currentStage?: DealStage
) =>
  getStagePositionOptions(
    stages,
    t,
    `${TRANSLATION_KEY}.createStage`,
    currentStage
  );

const CreateNewStage: React.FC<CreateNewStageProps> = ({
  isOpen,
  setIsOpen,
  onCreateStage
}) => {
  const { t } = useTranslation();
  const [milestoneHasError, setMilestoneHasError] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setIsFormValid(false);
    }
  }, [isOpen]);

  const [formSetValue, setFormSetValue] = useState<any>(null);

  const { data: dealStagesData } = useDealStages(true);
  const stages = dealStagesData?.stages || [];

  const positionOptions = useMemo(() => {
    return getPositionOptions(stages, t);
  }, [stages, t]);

  useEffect(() => {
    if (typeof formSetValue === "function") {
      formSetValue("positionInPipeline", StagePosition.LAST, {
        shouldValidate: true
      });
    }
  }, [formSetValue]);

  const formFields: FormFieldNewType[] = [
    {
      name: "name",
      type: "text",
      required: true,
      label: t(`${TRANSLATION_KEY}.createStage.name`),
      layout: "flex",
      fieldClassName: "flex-1",
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.createStage.namePlaceholder`)
      }
    },
    {
      name: "milestone",
      type: "singleSelect",
      required: true,
      label: t(`${TRANSLATION_KEY}.createStage.milestone`),
      layout: "flex",
      fieldClassName: "w-[200px]",
      helpText: t(`${TRANSLATION_KEY}.createStage.milestoneHelpText`),
      hasError: milestoneHasError,
      fieldActionProps: {
        options: getMilestoneOptions(t),
        placeholder: t(`${TRANSLATION_KEY}.createStage.chooseMilestone`),
        variant: "form",
        renderIcon: getMilestoneIcon
      }
    },
    // commented out - Not supported yet
    // {
    //   name: "stageDescription",
    //   type: "textarea",
    //   required: false,
    //   label: t(`${TRANSLATION_KEY}.createStage.stageDescription`),
    //   fieldActionProps: {
    //     placeholder: t(
    //       `${TRANSLATION_KEY}.createStage.stageDescriptionPlaceholder`
    //     ),
    //     minRows: 1,
    //   },
    // },
    {
      name: "positionInPipeline",
      type: "singleSelect",
      required: false,
      label: t(`${TRANSLATION_KEY}.createStage.positionInPipeline`),
      defaultValue: StagePosition.LAST,
      fieldActionProps: {
        options: positionOptions,
        placeholder: t(`${TRANSLATION_KEY}.createStage.selectPosition`),
        variant: "form",
        clearable: false
      }
    }
  ];

  const formSections: FormSectionProps[] = [
    {
      id: "name-and-milestone",
      fields: formFields.slice(0, 2), // name and milestone fields
      fieldsContainerClassName: "flex flex-row gap-6"
    },
    {
      id: "additional-fields",
      fields: formFields.slice(2)
    }
  ];

  const lastMilestoneValue = React.useRef<any>(null);

  const handleFormValidation = React.useCallback(
    (isValid: boolean, watchAllFields: any) => {
      setIsFormValid(isValid);
      const currentMilestone = watchAllFields?.milestone;
      if (lastMilestoneValue.current !== currentMilestone) {
        lastMilestoneValue.current = currentMilestone;
        setMilestoneHasError(!isEmpty(currentMilestone));
      }
    },
    []
  );

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
  };

  const handleSubmit = (data: newStageFormData) => {
    if (onCreateStage) {
      onCreateStage(data);
    }
    setIsOpen(false);
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      title={
        <Text variant="lgSubtitle">
          {t(`${TRANSLATION_KEY}.createStage.title`)}
        </Text>
      }
      content={
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <FormNew
            formRef={formRef}
            sections={formSections}
            isFormValid={handleFormValidation}
            onSubmit={handleSubmit}
            onSetValue={handleSetValue}
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setIsOpen(false)}>
            {t(`${TRANSLATION_KEY}.createStage.cancel`)}
          </Button>
          <Button
            variant="primary"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={!isFormValid}
          >
            {t(`${TRANSLATION_KEY}.createStage.createStage`)}
          </Button>
        </div>
      }
      contentClassname="w-[640px] max-w-[640px]"
    />
  );
};

export default CreateNewStage;
